// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AddItemToQuote from "@adl/App/Components/CustomQuote/AddItemToQuote";
import CalculateMargin from "@adl/App/Components/CustomQuote/CalculateMargin";
import addQuoteTocart from "@adl/App/Handlers/CustomQuote/AddCustomQuoteToCart";
import getQuoteDetails from "@adl/App/Handlers/CustomQuote/GetCustomQuote";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { color, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    customQuote: state.xMyCustomQuoteDetails.quote,
    productsInfo: state.xMyCustomQuoteDetails.productsInfo,
    sortBy: state.xMyCustomQuoteDetails.sortBy,
    editingSortOrder: state.xMyCustomQuoteDetails.editingSortOrder,
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    isLoading: state.xMyCustomQuoteDetails.isLoading,
});

const mapDispatchToProps = { getQuoteDetails, addQuoteTocart };

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyQuotesDetailsOptionsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    addItemsGridItem?: GridItemProps;
    addItemsGridContainer?: InjectableCss;
    sortByGridItem?: GridItemProps;
    calculateMarginGridItem?: GridItemProps;
    addItemsButton?: ButtonPresentationProps;
    closeAddItemsToQuote?: IconProps;
    editSortOrder?: CheckboxProps;
    sortOrderGridItem?: GridItemProps;
    addQuoteButton?: ButtonPresentationProps;
    actionButtonsGridItem?: GridItemProps;
}

export const myQuotesDetailsOptionsStyles: MyQuotesDetailsOptionsStyles = {
    wrapper: {
        css: css`
            @media print {
                display: none;
            }
            display: flex;
            margin: 16px 0;
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui5")};
        `,
    },
    container: {
        gap: 22,
    },
    addItemsGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            align-items: flex-end;
            justify-content: flex-start;
            ${xScreen("lte", "xs")} {
                justify-content: flex-start;
            }
        `,
    },
    addItemsGridContainer: {
        css: css`
            display: flex;
            width: 100%;
            position: relative;
        `,
    },
    addItemsButton: {
        variant: "primary",
        css: css`
            width: 100%;
            white-space: nowrap;
        `,
    },
    sortByGridItem: {
        width: [12, 12, 2, 2, 2],
        css: css`
            display: flex;
            justify-content: flex-start;
        `,
    },
    calculateMarginGridItem: {
        width: [6, 6, 2, 2, 2],
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            ${xScreen("lte", "sm")} {
                justify-content: flex-end;
                order: 4;
            }
        `,
    },
    closeAddItemsToQuote: {
        src: "X",
        size: 16,
        css: css`
            margin: 0;
            margin-top: 27px;
            cursor: pointer;
            &:hover {
                color: ${color("danger")};
            }
            ${xScreen("lte", "xs")} {
                margin: 0;
                position: absolute;
                top: -10px;
                right: 0;
            }
        `,
    },
    sortOrderGridItem: {
        width: [6, 6, 2, 2, 2],
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            ${xScreen("lte", "sm")} {
                justify-content: flex-start;
                order: 4;
            }
        `,
    },
    editSortOrder: {
        css: css`
            cursor: pointer;
        `,
    },
    actionButtonsGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            ${xScreen("lte", "sm")} {
                order: 3;
            }
        `,
    },
    addQuoteButton: {
        css: css`
            padding: 0 15px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

const styles = myQuotesDetailsOptionsStyles;

const MyCustomQuoteDetailsOptions = ({
    customQuote,
    productsInfo,
    sortBy,
    getQuoteDetails,
    editingSortOrder,
    rfqMyQuotesPageUrl,
    addQuoteTocart,
    history,
    isLoading,
}: Props) => {
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);
    const [addItemsToQuoteIsOpen, setAddItemsToQuoteIsOpen] = useState(false);
    const [sortOptions, setSortOptions] = useState<OptionObject[]>([
        { optionValue: "", optionText: "" },
        { optionValue: "name asc", optionText: "Product: A - Z" },
        { optionValue: "name desc", optionText: "Product: Z - A" },
    ]);

    const [selectedSortBy, setSelectedSortBy] = useState(sortBy);

    if (!customQuote || isLoading) {
        return null;
    }

    const addToCartClickHandler = (e: any) => {
        addQuoteTocart({
            quoteId: customQuote.id,
            onSuccess: result => {
                toasterContext.addToast({ body: translate("All Items Added To Cart"), messageType: "success" });
            },
        });
    };

    const returnToAssociatedQuote = () => {
        if (customQuote.quoteIdReference) {
            history.push(`${rfqMyQuotesPageUrl}?quoteId=${customQuote.quoteIdReference}`);
        }
    };

    const sortByChangeHandler = (value?: string) => {
        setSelectedSortBy(value as any);
        getQuoteDetails({
            quoteId: customQuote.id,
            skipPricingAndInventory: false,
            sortBy: value,
        });
    };

    const addItemsToQuoteClickHandler = () => {
        setAddItemsToQuoteIsOpen(true);
    };

    const editingSortOrderChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        dispatch({
            type: "Pages/xMyCustomQuotes/SetEditingSortOrder",
            editingSortOrder: value,
        });
    };
    // largest sortOrder in lines + 1

    const nextLineSortOrder = customQuote.linesModel
        ? Math.max(...customQuote.linesModel.map(line => line.sortOrder)) + 1
        : 1;

    const isEmptyQuote = !customQuote.linesModel || customQuote.linesModel.length === 0;
    const canAddToCart = !customQuote.allowRedirectToAssociatedQuote && !customQuote.isCameFromNormalQuote;

    return (
        <StyledWrapper {...styles.wrapper}>
            {!addItemsToQuoteIsOpen && (
                <GridContainer {...styles.container}>
                    <GridItem {...styles.addItemsGridItem}>
                        <Button
                            {...styles.addItemsButton}
                            onClick={addItemsToQuoteClickHandler}
                            data-test-selector="myQuoteDetails_addItemsToQuote"
                        >
                            {translate("Add Items")}
                        </Button>
                    </GridItem>
                    <GridItem {...styles.sortByGridItem}>
                        <DynamicDropdown
                            data-test-selector="myCustomQuotesDetails_sortControl"
                            options={sortOptions}
                            selected={selectedSortBy}
                            onSelectionChange={sortByChangeHandler}
                            disabled={isEmptyQuote}
                        />
                    </GridItem>
                    <GridItem {...styles.sortOrderGridItem}>
                        <Checkbox
                            {...styles.editSortOrder}
                            checked={editingSortOrder}
                            onChange={editingSortOrderChangeHandler}
                            data-test-selector="myCustomQuoteDetailPage_editSortOrderCheckbox"
                            disabled={customQuote.linesCount < 1}
                        >
                            {translate("Edit Sort Order")}
                        </Checkbox>
                    </GridItem>
                    <GridItem {...styles.calculateMarginGridItem}>
                        <CalculateMargin
                            isApplyAllMargins={true}
                            productsInfos={productsInfo}
                            quoteId={customQuote.id}
                            quoteLines={customQuote.linesModel}
                        />
                    </GridItem>
                    <GridItem {...styles.actionButtonsGridItem}>
                        {canAddToCart ? (
                            <Button
                                disabled={isEmptyQuote}
                                {...styles.addQuoteButton}
                                onClick={addToCartClickHandler}
                                data-test-selector="addCustomQuoteToCart"
                                variant="secondary"
                            >
                                {translate("Add Quote to Cart")}
                            </Button>
                        ) : (
                            <Button
                                {...styles.addQuoteButton}
                                onClick={returnToAssociatedQuote}
                                data-test-selector="addCustomQuoteToCart"
                                variant="secondary"
                            >
                                {translate("Return to Associated Quote")}
                            </Button>
                        )}
                    </GridItem>
                </GridContainer>
            )}
            {addItemsToQuoteIsOpen && (
                <StyledWrapper {...styles.addItemsGridContainer}>
                    <AddItemToQuote quoteId={customQuote.id} sortOrder={nextLineSortOrder} />
                    <Icon
                        {...styles.closeAddItemsToQuote}
                        onClick={() => {
                            setAddItemsToQuoteIsOpen(false);
                        }}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuoteDetailsOptions)),
    definition: {
        group: "Custom Quote Details" as any,
        displayName: "Options",
        allowedContexts: ["CustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
